(() => {
  if (customElements.get('vinut-flavor-tabs')) return;
  let vendor;
  const loadVendor = source => vendor || (vendor = import(source));

  class FlavorTabs extends HTMLElement {
    constructor() {
      super();
      this.mount = this.mount.bind(this);
      this.schedule = this.schedule.bind(this);
      this.onFocus = event => {
        const tab = event.target.closest('[data-cc-flavor-tab-button]');
        if (tab && !this.loop) this.reveal(tab);
      };
      this.version = 0;
    }

    connectedCallback() {
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', this.mount, { once: true });
      else this.mount();
    }

    mount() {
      if (!this.isConnected || this.mounted) return;
      this.viewport = this.querySelector('.cc-flavor-tabs__viewport');
      this.list = this.querySelector('.cc-flavor-tabs__tabs');
      if (!this.viewport || !this.list) return;
      this.buttons = [...this.list.querySelectorAll('[data-cc-flavor-tab-button]')];
      this.slides = [...this.list.children];
      this.mounted = true;
      this.addEventListener('focusin', this.onFocus);
      window.addEventListener('resize', this.schedule, { passive: true });
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.schedule);
        this.observer.observe(this.viewport);
      }
      this.updateMode();
    }

    schedule() {
      if (!this.isConnected || !this.mounted || this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.updateMode(); });
    }

    updateMode() {
      const mode = innerWidth < 750 ? 'mobile' : innerWidth < 1200 ? 'tablet' : 'desktop';
      if (mode !== this.mode) {
        this.stopLoop();
        this.mode = mode;
        this.list.scrollLeft = 0;
        this.viewport.scrollLeft = 0;
        const selected = this.buttons.find(button => button.getAttribute('aria-selected') === 'true');
        if (selected && mode !== 'mobile') this.reveal(selected);
        this.layoutChanged();
      }
      // Desktop shares the mobile loop; tablet keeps all categories visible.
      if (mode !== 'tablet' && !this.loop && !this.loading && this.viewport.clientWidth) this.startLoop();
    }

    async startLoop() {
      this.loading = true;
      const version = ++this.version;
      try {
        const { Splide } = await loadVendor(this.dataset.carouselSrc);
        if (!this.mounted || !this.isConnected || this.mode === 'tablet' || version !== this.version) return;
        const selected = this.buttons.findIndex(button => button.getAttribute('aria-selected') === 'true');
        this.classList.add('splide');
        this.viewport.classList.add('splide__track');
        this.list.classList.add('splide__list');
        this.slides.forEach(slide => slide.classList.add('splide__slide'));
        this.list.scrollLeft = 0;
        this.loop = new Splide(this, {
          type: 'loop', perPage: 3, perMove: 1, clones: 3, start: Math.max(0, selected - 2),
          arrows: false, pagination: false, autoplay: false, keyboard: false, drag: true,
          role: '', live: false, slideFocus: false, focusableNodes: '', cloneStatus: false,
          speed: 220, waitForTransition: true, flickMaxPages: 1, flickPower: 1, gap: 0,
          reducedMotion: { speed: 0, rewindSpeed: 0 }
        });
        this.setAttribute('data-loop-ready', '');
        this.loop.mount();
        this.loop.on('moved', () => { this.syncSemantics(); this.layoutChanged(); });
        this.loop.on('refresh resized', () => {
          cancelAnimationFrame(this.syncFrame);
          this.syncFrame = requestAnimationFrame(() => {
            if (this.loop && this.mounted) { this.syncSemantics(); this.layoutChanged(); }
          });
        });
        this.syncSemantics();
        this.layoutChanged();
      } catch (error) {
        // Native horizontal scrolling stays available if the existing carousel asset cannot load.
        this.loopError = error.message;
        this.stopLoop();
      } finally {
        if (version === this.version) this.loading = false;
      }
    }

    visibleButton(button) {
      const box = this.viewport.getBoundingClientRect();
      const key = button.getAttribute('aria-controls');
      return [...this.list.querySelectorAll('[data-cc-flavor-tab-button]')].find(candidate => {
        if (candidate.getAttribute('aria-controls') !== key) return false;
        const rect = candidate.getBoundingClientRect();
        return rect.left >= box.left - 1 && rect.right <= box.right + 1;
      });
    }

    syncSemantics() {
      this.viewport.setAttribute('role', 'tablist');
      this.list.setAttribute('role', 'presentation');
      const selected = this.buttons.find(button => button.getAttribute('aria-selected') === 'true');
      const selectedKey = selected?.getAttribute('aria-controls');
      const focusTarget = selected && (this.visibleButton(selected) || selected);
      const accessibleButtons = new Set(this.buttons.map(button => this.visibleButton(button) || button));
      [...this.list.children].forEach((slide, index) => {
        slide.setAttribute('role', 'presentation');
        slide.removeAttribute('aria-label');
        slide.removeAttribute('aria-roledescription');
        const button = slide.querySelector('[data-cc-flavor-tab-button]');
        if (!button) return;
        slide.setAttribute('aria-hidden', String(!accessibleButtons.has(button)));
        const original = this.buttons.find(item => item.getAttribute('aria-controls') === button.getAttribute('aria-controls'));
        if (button !== original) button.id = original.id + '-copy-' + index;
        const active = button.getAttribute('aria-controls') === selectedKey;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-selected', String(active));
        button.classList.toggle('is-active', active);
        button.tabIndex = button === focusTarget ? 0 : -1;
      });
      const focused = document.activeElement;
      if (focusTarget && focused !== focusTarget && this.contains(focused) && focused.getAttribute('aria-controls') === selectedKey) {
        focusTarget.focus({ preventScroll: true });
      }
      if (this.pendingFocus) {
        const target = this.visibleButton(this.pendingFocus);
        if (target) { this.pendingFocus = null; target.focus({ preventScroll: true }); }
      }
    }

    reveal(button, clickedButton) {
      if (!this.list) return;
      if (this.loop) {
        const index = this.buttons.findIndex(item => item.getAttribute('aria-controls') === button.getAttribute('aria-controls'));
        const offset = (index - this.loop.index + this.buttons.length) % this.buttons.length;
        if (clickedButton) {
          const box = this.viewport.getBoundingClientRect();
          const slot = this.viewport.clientWidth / 3;
          // Advance once from either the middle or rightmost visible slot.
          if (clickedButton.getBoundingClientRect().left - box.left > slot * 0.5) this.loop.go('>');
        } else if (offset > 2) this.loop.go(index);
        this.viewport.scrollLeft = 0;
        this.syncSemantics();
        return;
      }
      if (this.mode === 'tablet') return;
      const viewport = this.list.getBoundingClientRect();
      const item = button.getBoundingClientRect();
      if (item.left < viewport.left - 1) this.list.scrollBy({ left: item.left - viewport.left, behavior: 'auto' });
      else if (item.right > viewport.right + 1) this.list.scrollBy({ left: item.right - viewport.right, behavior: 'auto' });
    }

    focusTab(button) {
      const target = this.loop ? this.visibleButton(button) : button;
      if (target) target.focus({ preventScroll: true });
      else this.pendingFocus = button;
    }

    layoutChanged() {
      this.dispatchEvent(new CustomEvent('cc:flavor-tabs:layout', { bubbles: true }));
    }

    stopLoop() {
      this.version++;
      this.loading = false;
      this.pendingFocus = null;
      cancelAnimationFrame(this.syncFrame);
      if (this.loop) { this.loop.destroy(true); this.loop = null; }
      this.classList.remove('splide');
      this.removeAttribute('data-loop-ready');
      this.viewport?.classList.remove('splide__track');
      this.list?.classList.remove('splide__list');
      this.slides?.forEach(slide => slide.classList.remove('splide__slide'));
      if (this.buttons) {
        this.syncSemantics();
        this.slides.forEach(slide => slide.removeAttribute('aria-hidden'));
      }
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.mount);
      if (!this.mounted) return;
      this.mounted = false;
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.removeEventListener('focusin', this.onFocus);
      window.removeEventListener('resize', this.schedule);
      this.stopLoop();
    }
  }
  customElements.define('vinut-flavor-tabs', FlavorTabs);
})();
